import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Checkbox,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import {
  AlertTriangle,
  Loader2,
  RefreshCw,
  RotateCcw,
  Search,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';

import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { useStaleSafeLoader } from '../../hooks/useStaleSafeLoader';
import { getTaskTypes, updateTaskTypes } from '../../services/taskTypesApi';
import type {
  TaskTypeRowResponse,
  VisitTypeId,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { disciplineName, groupByDiscipline } from '../../constants/disciplines';

/**
 * Admin screen for which task types are covered care and which are paperwork.
 *
 * The checkbox states a default, not a verdict on work already scheduled. A
 * visit stores its own billing status when it is created, so ticking a box here
 * changes what the next visit of that type will be and leaves the ones already
 * on the calendar exactly as they were. The screen says so rather than leaving
 * a manager to discover it from a claim.
 *
 * Rows Roger classifies and rows the agency has classified are shown together
 * with their provenance, because "non-billable" and "we decided non-billable"
 * read identically on a checkbox and only the second is the agency's to explain.
 */
const TaskTypesSection: React.FC = () => {
  const { canEdit } = useAdminSectionAccess('task-types');
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [rows, setRows] = useState<TaskTypeRowResponse[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  // Rows the manager has changed but not saved, keyed by task type. A null
  // value is a reset: withdraw our answer so the row follows Roger's set.
  const [edits, setEdits] = useState<Record<string, boolean | null>>({});

  // Monotonic load generation; only the newest load may write to the table.
  const { isLoading, loadFailed, run: runLoad } = useStaleSafeLoader();

  const load = useCallback(async () => {
    await runLoad(
      async () => {
        // throwOnError so HTTP failures reach the catch below; the client's
        // default (false) resolves with an error result whose `data` is
        // undefined, which would render a failed load as an empty grid.
        const result = await getTaskTypes({ throwOnError: true });
        // Belt and braces against the same default: if a response ever arrives
        // without data, that is a failure, not an agency with no task types.
        // `?? []` here would render it as an empty grid.
        if (!result.data)
          throw new Error('Task types response carried no data');
        return result.data.rows;
      },
      {
        trackLoadFailed: true,
        onSuccess: nextRows => {
          setRows(nextRows);
          setEdits({});
        },
        onError: error => {
          console.error('Failed to load task types', error);
        },
      }
    );
  }, [runLoad]);

  useEffect(() => {
    void load();
  }, [load]);

  const effectiveNonBillable = useCallback(
    (row: TaskTypeRowResponse): boolean => {
      const edit = edits[row.visit_type];
      // A pending reset shows Roger's answer, which is where saving lands it.
      if (edit === null) return row.roger_default_non_billable;
      return edit ?? row.non_billable;
    },
    [edits]
  );

  const toggle = useCallback((row: TaskTypeRowResponse, checked: boolean) => {
    setEdits(current => {
      const next = { ...current };
      if (checked === row.non_billable) {
        // Back to where it started, so there is nothing left to save for this
        // row. Sending it anyway would write an agency row that only restates
        // what was already true, and turn a "Roger default" badge into an
        // "Agency" one the manager never asked for.
        delete next[row.visit_type];
      } else {
        next[row.visit_type] = checked;
      }
      return next;
    });
  }, []);

  const reset = useCallback((row: TaskTypeRowResponse) => {
    setEdits(current => ({ ...current, [row.visit_type]: null }));
  }, []);

  const changeCount = Object.keys(edits).length;

  const save = useCallback(async () => {
    if (changeCount === 0) return;
    setIsSaving(true);
    try {
      // throwOnError for the same reason, and it matters more here: without
      // it a refused save resolves, so the grid would clear the manager's
      // pending ticks and report success for a change the server rejected.
      const result = await updateTaskTypes({
        body: {
          changes: Object.entries(edits)
            .filter(([, nonBillable]) => nonBillable !== null)
            .map(([visitType, nonBillable]) => ({
              visit_type: visitType as VisitTypeId,
              non_billable: nonBillable as boolean,
            })),
          resets: Object.entries(edits)
            .filter(([, nonBillable]) => nonBillable === null)
            .map(([visitType]) => visitType as VisitTypeId),
        },
        throwOnError: true,
      });
      // Same reason, and it costs more here: falling through with no data
      // would clear the pending ticks and report a save that did not happen.
      if (!result.data) throw new Error('Task types response carried no data');
      setRows(result.data.rows);
      setEdits({});
      showSuccess('Task types saved');
    } catch (error) {
      console.error('Failed to save task types', error);
      showError(getBusinessErrorMessage(error) ?? 'Failed to save task types');
    } finally {
      setIsSaving(false);
    }
  }, [changeCount, edits, showError, showSuccess]);

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matching = term
      ? rows.filter(row => row.display_name.toLowerCase().includes(term))
      : rows;

    return groupByDiscipline(matching, row => row.discipline);
  }, [rows, search]);

  if (isLoading && rows.length === 0) {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading task types...
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className='space-y-3'>
        <div className='flex items-center gap-2 text-sm text-destructive'>
          <AlertTriangle className='h-4 w-4' />
          Unable to load task types.
        </div>
        <Button variant='outline' size='sm' onClick={() => void load()}>
          <RefreshCw className='mr-2 h-4 w-4' />
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='space-y-1'>
        <h2 className='text-lg font-semibold'>Task Types</h2>
        <p className='text-sm text-muted-foreground'>
          A non-billable task type is paperwork rather than a visit. It produces
          no claim line, uses no authorized visit, does not count toward LUPA,
          and does not appear in Payroll Manager. Claims, authorizations and
          LUPA follow the status each visit was created with, so visits already
          on the calendar are unaffected. Payroll follows the task type, so
          visits not yet included in a payroll run leave the list as soon as you
          save.
        </p>
      </div>

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='relative w-full sm:w-72'>
          <Search className='absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            inputSize='sm'
            className='pl-8'
            placeholder='Search task types'
            value={search}
            onChange={event => setSearch(event.target.value)}
          />
        </div>
        {canEdit && (
          <div className='flex flex-wrap items-center gap-2'>
            {changeCount > 0 && (
              <span className='text-sm text-muted-foreground'>
                {changeCount} unsaved {changeCount === 1 ? 'change' : 'changes'}
              </span>
            )}
            <Button
              variant='outline'
              size='sm'
              disabled={changeCount === 0 || isSaving}
              onClick={() => setEdits({})}
            >
              Cancel
            </Button>
            <Button
              size='sm'
              disabled={changeCount === 0 || isSaving}
              onClick={() => void save()}
            >
              {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Save
            </Button>
          </div>
        )}
      </div>

      {groups.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          No task types match that search.
        </p>
      ) : (
        <Accordion
          type='multiple'
          defaultValue={groups.map(group => group.discipline)}
        >
          {groups.map(({ discipline, items: disciplineRows }) => (
            <AccordionItem key={discipline} value={discipline}>
              <AccordionTrigger>
                <span className='flex items-center gap-2'>
                  {disciplineName(discipline)}
                  <Badge variant='secondary'>{disciplineRows.length}</Badge>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <div className='overflow-x-auto'>
                  <Table className='min-w-[480px]'>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task type</TableHead>
                        <TableHead className='w-32'>Non-billable</TableHead>
                        <TableHead className='w-36'>Set by</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {disciplineRows.map(row => {
                        const checked = effectiveNonBillable(row);
                        const isChanged = row.visit_type in edits;
                        return (
                          <TableRow key={row.visit_type}>
                            <TableCell>{row.display_name}</TableCell>
                            <TableCell>
                              <Checkbox
                                checkboxSize='sm'
                                aria-label={`Mark ${row.display_name} non-billable`}
                                checked={checked}
                                disabled={!canEdit || isSaving}
                                data-pending={isChanged || undefined}
                                onCheckedChange={value =>
                                  toggle(row, value === true)
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <div className='flex items-center gap-2'>
                                <Badge
                                  variant={
                                    isChanged
                                      ? 'default'
                                      : row.source === 'agency'
                                        ? 'secondary'
                                        : 'outline'
                                  }
                                  className='whitespace-nowrap'
                                >
                                  {isChanged
                                    ? 'Unsaved'
                                    : row.source === 'agency'
                                      ? 'Your organization'
                                      : 'Roger default'}
                                </Badge>
                                {canEdit && row.source === 'agency' && (
                                  <Button
                                    variant='ghost'
                                    size='sm'
                                    disabled={isSaving}
                                    title='Follow Roger default'
                                    aria-label={`Reset ${row.display_name} to Roger default`}
                                    onClick={() => reset(row)}
                                  >
                                    <RotateCcw className='h-3.5 w-3.5' />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
};

export default TaskTypesSection;
